import React, { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import './CompanyHistory.css';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';

// 전자구매 자료실 컴포넌트
const EProcurementCenter = () => {
  const [posts, setPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const postsPerPage = 10;

  // Firebase에서 게시글 실시간 로드
  useEffect(() => {
    const postsRef = collection(db, 'ko-e-procurement');
    const q = query(postsRef, orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const postsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPosts(postsData);
    });

    // 컴포넌트 언마운트 시 구독 해제
    return () => unsubscribe();
  }, []);

  // 검색 필터링
  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 페이지네이션
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const currentPosts = filteredPosts.slice(startIndex, startIndex + postsPerPage);

  // PDF 다운로드
  const handleDownload = (post) => {
    if (post.pdfUrl) {
      window.open(post.pdfUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (post.pdfFile) {
      const link = document.createElement('a');
      link.href = post.pdfFile;
      link.download = post.pdfFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // 게시글 클릭 시 비밀번호 확인 또는 팝업 표시
  const handlePostClick = async (post) => {
    setSelectedPost(post);
    
    // 비밀번호가 설정된 게시글인 경우 비밀번호 입력 모달 표시
    if (post.password && post.password.trim() !== '') {
      setShowPasswordModal(true);
      setPasswordInput('');
    } else {
      // 비밀번호가 없는 경우 바로 팝업 표시
      setShowPopup(true);
      await incrementViewCount(post.id);
    }
  };

  // 조회수 증가 함수
  const incrementViewCount = async (postId) => {
    try {
      const { doc, updateDoc, increment } = await import('firebase/firestore');
      const postRef = doc(db, 'ko-e-procurement', postId);
      await updateDoc(postRef, {
        viewCount: increment(1)
      });
    } catch (error) {
      console.error('조회수 증가 실패:', error);
    }
  };

  // 비밀번호 확인
  const handlePasswordSubmit = async () => {
    if (passwordInput === selectedPost.password) {
      setShowPasswordModal(false);
      setShowPopup(true);
      await incrementViewCount(selectedPost.id);
    } else {
      alert('비밀번호가 올바르지 않습니다.');
    }
  };

  // 팝업 닫기
  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedPost(null);
  };

  // 비밀번호 모달 닫기
  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setSelectedPost(null);
    setPasswordInput('');
  };

  // 페이지 변경
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="board-container">
      {/* 게시판 헤더 */}
      <div className="board-header">
        <h2>🛒 전자구매</h2>
        <p>아래 양식을 채우셔서 전자우편이나 팩스, 우편으로 문의 바랍니다. (팩스 032-819-0518 전화 032-811-0511)</p>
      </div>

      {/* 게시글 목록 */}
      <div className="board-content">
        <div className="board-table">
          <table>
            <thead>
              <tr>
                <th className="col-number">번호</th>
                <th className="col-title">제목</th>
                <th className="col-author">작성자</th>
                <th className="col-date">작성일</th>
                <th className="col-views">조회수</th>
              </tr>
            </thead>
            <tbody>
              {currentPosts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="empty-message">
                    <div className="empty-state">
                      <span>📄</span>
                      <p>등록된 자료가 없습니다.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentPosts.map((post, index) => (
                  <tr key={post.id} onClick={() => handlePostClick(post)} className="post-row">
                    <td className="post-number">{startIndex + index + 1}</td>
                    <td className="post-title">
                      <span className="title-text">{post.title?.replace(/[^\w\s가-힣]/g, '') || '제목 없음'}</span>
                    </td>
                    <td className="post-author">관리자</td>
                    <td className="post-date">
                      {post.createdAt ? 
                        new Date(post.createdAt.seconds ? post.createdAt.seconds * 1000 : post.createdAt).toLocaleDateString('ko-KR') : 
                        '날짜 없음'
                      }
                    </td>
                    <td className="post-views">{post.viewCount || 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="board-pagination">
            <div className="pagination-info">
              총 {filteredPosts.length}개의 자료
            </div>
            <div className="pagination-buttons">
              <button 
                className="page-btn prev-btn"
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                이전
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const startPage = Math.max(1, currentPage - 2);
                const page = startPage + i;
                if (page > totalPages) return null;
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`page-btn ${currentPage === page ? 'active' : ''}`}
                  >
                    {page}
                  </button>
                );
              })}
              <button 
                className="page-btn next-btn"
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 검색 영역 */}
      <div className="board-search">
        <div className="search-container">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="자료 제목이나 내용을 검색하세요..."
            className="search-input"
          />
          <button className="search-btn">
            <span>🔍</span>
            검색
          </button>
        </div>
      </div>

      {/* 비밀번호 입력 모달 */}
      {showPasswordModal && selectedPost && (
        <div className="post-popup-overlay" onClick={handleClosePasswordModal}>
          <div className="password-modal" onClick={(e) => e.stopPropagation()}>
            <div className="password-header">
              <h3>🔒 비밀번호 입력</h3>
              <button className="close-btn" onClick={handleClosePasswordModal}>
                ✕
              </button>
            </div>
            <div className="password-content">
              <p>이 게시글은 비밀번호가 설정되어 있습니다.</p>
              <div className="password-input-group">
                <input
                  type="password"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  placeholder="비밀번호를 입력하세요"
                  className="password-input"
                  onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                />
                <button onClick={handlePasswordSubmit} className="password-submit-btn">
                  확인
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 게시글 상세 팝업 */}
      {showPopup && selectedPost && (
        <div className="post-popup-overlay" onClick={handleClosePopup}>
          <div className="post-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h3>{selectedPost.title?.replace(/[^\w\s가-힣]/g, '') || '제목 없음'}</h3>
              <button className="close-btn" onClick={handleClosePopup}>
                ✕
              </button>
            </div>
            <div className="popup-content">
              <div className="post-meta">
                <span className="meta-item">
                  <strong>작성자:</strong> 관리자
                </span>
                <span className="meta-item">
                  <strong>작성일:</strong> {
                    selectedPost.createdAt ? 
                      new Date(selectedPost.createdAt.seconds ? selectedPost.createdAt.seconds * 1000 : selectedPost.createdAt).toLocaleDateString('ko-KR') : 
                      '날짜 없음'
                  }
                </span>
                <span className="meta-item">
                  <strong>조회수:</strong> {selectedPost.viewCount || 0}
                </span>
              </div>
              <div className="post-body">
                <p>{selectedPost.content}</p>
              </div>
              {selectedPost.pdfFileName && (
                <div className="popup-actions">
                  <button 
                    onClick={() => handleDownload(selectedPost)}
                    className="download-btn"
                  >
                    📄 {selectedPost.pdfFileName} 다운로드
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EProcurement = () => {
  const [activeSection, setActiveSection] = useState('e-procurement-center');
  
  const sidebarItems = [
    { name: '전자구매', path: 'e-procurement-center' }
  ];

  // 스크롤 스파이 기능
  useEffect(() => {
    const handleScroll = () => {
      const sections = sidebarItems.map(item => item.path);
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sidebarItems]);

  // 해시 URL 처리 (페이지 로드 시 해당 섹션으로 이동)
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && sidebarItems.some(item => item.path === hash)) {
      setActiveSection(hash);
      setTimeout(() => {
        scrollToSection(hash);
      }, 100);
    }
  }, [sidebarItems]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 100; // 헤더 높이
      const elementPosition = element.offsetTop - headerHeight - 30; // 헤더 높이 + 여백
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    scrollToSection(sectionId);
  };

  return (
    <>
      <Sidebar 
        category="전자구매" 
        items={sidebarItems}
        activeSection={activeSection}
        onItemClick={handleSectionChange}
      />
      <div className="company-history-page">
        <div className="history-container">
          <h1 className="page-category-title">전자구매</h1>
          
          {/* 전자구매 섹션 */}
          <section id="e-procurement-center" className="section">
            <h2 className="section-title">전자구매</h2>
            <div className="section-container">
              <EProcurementCenter />
            </div>
          </section>

        </div>
      </div>
    </>
  );
};

export default EProcurement;